import { Dispatch, SetStateAction } from "react";

import { appSwitcherPopoutContainerCss } from "@/base/NewselaGlobalLayoutV2/NavigationBar/AppSwitcherMenu/AppSwitcherPopout/AppSwitcherPopout.styles";
import { PopoutRow } from "@/base/NewselaGlobalLayoutV2/NavigationBar/AppSwitcherMenu/PopoutRow/PopoutRow";
import { FormativeLogo } from "@/base/NewselaGlobalLayoutV2/NavigationBar/AppSwitcherMenu/PopoutRow/assets/FormativeLogo";
import { titleCss } from "@/base/NewselaGlobalLayoutV2/NavigationBar/AppSwitcherMenu/PopoutRow/PopoutRow.styles";
import useGetUserProperties from "@/users/v3/useGetUserProperties";
import getEverwriteAppUrl from "@/utils/getEverwriteAppUrl";

const DefaultTitleComponent = ({ title }: { title: string }) => (
  <div css={titleCss}>{title}</div>
);

const AppSwitcherPopout = ({ setIsOpen }: IAppSwitcherPopout) => {
  const { canSeeFormative, canSeeEverwrite } = useGetUserProperties();

  const onKeyDown = ({ key }: KeyboardEvent) => {
    if (key === "Escape") {
      setIsOpen(false);
    }
  };

  const getEverwriteUrl = () => {
    // The Everwrite URL in the navbar changes depending on the user's licensed state
    // as well as what environment they're in. A user requires a premium license in
    // order to see Everwrite functionality; otherwise, they should be directed to
    // the Newsela Writing marketing page.
    if (!canSeeEverwrite) {
      return "https://newsela.com/about/products/writing/";
    }

    // Otherwise, ensure we're properly honoring environment by linking to the correct
    // Everwrite env URL. Production should go to production, staging should go to
    // staging, and all other environments -- dev, feature, local, etc. -- should go to
    // dev.
    return getEverwriteAppUrl();
  };

  const everwriteUrl = getEverwriteUrl();

  return (
    <ul css={appSwitcherPopoutContainerCss} data-testid="app-switcher-menu">
      {canSeeFormative && (
        <PopoutRow
          label="Go to Formative by Newsela (opens in new tab)"
          onKeyDown={onKeyDown}
          dataQASelector="global-nav-app-switcher-formative"
          href="https://app.formative.com/home"
          TitleComponent={
            <FormativeLogo id="formative" ariaLabel="Formative by Newsela" />
          }
          description="An interactive and collaborative assessments app with real-time feedback and progress tracking."
        />
      )}

      {canSeeEverwrite && (
        <PopoutRow
          label="Go to Newsela Writing (opens in new tab)"
          onKeyDown={onKeyDown}
          dataQASelector="global-nav-go-to-everwrite"
          href={everwriteUrl}
          TitleComponent={<DefaultTitleComponent title="Newsela Writing" />}
          description="Your assistant to help students become confident writers."
        />
      )}
    </ul>
  );
};

interface IAppSwitcherPopout {
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

AppSwitcherPopout.displayName = "AppSwitcherPopout";
export { AppSwitcherPopout };
